import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Button,
  Icon,
  Text,
  BlockStack,
  InlineStack,
  Select,
  TextField,
  Collapsible,
} from '@shopify/polaris';
import {
  ChevronLeftMinor,
  ChevronRightMinor,
  FileMinor,
  ImageMajor,
  MagicMajor,
} from '@shopify/polaris-icons';
import { authService } from '../../services/authService';
import { getServerURL } from '../../services/config';
import AuthenticatedAssetImage from '../AuthenticatedAssetImage';

const SERVER_URL = getServerURL();

const parseModelOptions = (rawValue) => {
  const raw = String(rawValue || '');
  const values = raw
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean);
  return Array.from(new Set(values));
};

const extractEnabledModelOptions = (settingsPayload = {}) => {
  const catalogValues = Array.isArray(settingsPayload?.REGEN_MODEL_CATALOG)
    ? settingsPayload.REGEN_MODEL_CATALOG
      .filter((item) => item && item.enabled !== false && item?.capabilities?.regeneration !== false)
      .map((item) => {
        const value = String(item.value || '').trim();
        if (!value) return null;
        const label = String(item.label || value).trim() || value;
        return { label, value };
      })
      .filter(Boolean)
    : [];

  if (catalogValues.length > 0) {
    const deduped = [];
    const seen = new Set();
    catalogValues.forEach((item) => {
      const key = String(item.value || '').trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      deduped.push(item);
    });
    return deduped;
  }

  return parseModelOptions(settingsPayload?.REGEN_MODEL_OPTIONS || '').map((value) => ({ label: value, value }));
};

const normalizeProviderValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'vertex' || normalized === 'gemini') return 'vertex';
  if (normalized === 'comfyui' || normalized === 'flux2') return 'comfyui';
  if (normalized === 'fal-ai' || normalized === 'fal.ai' || normalized === 'fal-flux-max' || normalized === 'fal-ai-flux2-dev' || normalized === 'fal-flux2-dev' || normalized === 'flux2-dev') return 'fal-ai';
  return normalized;
};

const providerLabel = (value) => {
  const normalized = normalizeProviderValue(value);
  if (normalized === 'vertex') return 'Vertex';
  if (normalized === 'comfyui') return 'ComfyUI';
  if (normalized === 'fal-ai') return 'FAL.AI';
  return normalized || 'Unknown';
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 12,
  lineHeight: '18px',
  border: '1px solid #dfe3e8',
  background: '#f6f6f7',
  color: '#202223',
};

const getResolvedModelLabel = (responseData = {}) => {
  const customResolved = String(responseData?.customModelResolved || '').trim();
  if (customResolved) return customResolved;
  const modelUsed = responseData?.modelUsed;
  if (!modelUsed || typeof modelUsed !== 'object') return '';
  return String(modelUsed.multi || modelUsed.single || modelUsed.model || '').trim();
};

const ViewPreviewModal = ({
  isOpen,
  onClose,
  selectedPreview,
  setToastMessage,
  setToastActive
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloading38x19, setIsDownloading38x19] = useState(false);
  const [regeneratingBySceneOrder, setRegeneratingBySceneOrder] = useState({});
  const activeSceneRegenerationsRef = useRef(new Set());
  const [regeneratedImageUrlsBySceneOrder, setRegeneratedImageUrlsBySceneOrder] = useState({});
  const [customPrompts, setCustomPrompts] = useState({});
  const [selectedModelsBySceneKey, setSelectedModelsBySceneKey] = useState({});
  const [regenerationModelOptions, setRegenerationModelOptions] = useState([]);
  const [defaultRegenerationProvider, setDefaultRegenerationProvider] = useState('vertex');
  const [defaultRegenerationModel, setDefaultRegenerationModel] = useState('');
  const [regenerationMetaBySceneKey, setRegenerationMetaBySceneKey] = useState({});
  const [expandedCustomPrompt, setExpandedCustomPrompt] = useState({});
  const [expandedEditBySceneKey, setExpandedEditBySceneKey] = useState({});
  const [editPromptsBySceneKey, setEditPromptsBySceneKey] = useState({});
  const [editModelsBySceneKey, setEditModelsBySceneKey] = useState({});
  const [editingBySceneKey, setEditingBySceneKey] = useState({});
  const activeSceneEditsRef = useRef(new Set());

  const regenerationModelSelectOptions = useMemo(() => {
    const storyOverrideModel = String(selectedPreview?.story?.regeneration_model_override || selectedPreview?.regeneration_model_override || '').trim();
    const options = [...regenerationModelOptions];
    const values = new Set(options.map((item) => item.value));
    if (defaultRegenerationModel && !values.has(defaultRegenerationModel)) {
      options.push({ label: defaultRegenerationModel, value: defaultRegenerationModel });
      values.add(defaultRegenerationModel);
    }
    if (storyOverrideModel && !values.has(storyOverrideModel)) {
      options.push({ label: storyOverrideModel, value: storyOverrideModel });
    }

    return [
      { label: 'Use story/global default', value: '' },
      ...options,
    ];
  }, [regenerationModelOptions, defaultRegenerationModel, selectedPreview]);

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;

    const loadRegenerationModelSettings = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/settings/generation/regeneration-models`, {
          headers: authService.getAuthHeaders(),
        });
        if (!response.ok) return;
        const result = await response.json();
        const settingsDoc = result?.data || null;
        if (!isMounted) return;
        setDefaultRegenerationProvider(normalizeProviderValue(settingsDoc?.REGEN_DEFAULT_DYNAMIC_PROVIDER || 'vertex') || 'vertex');
        setDefaultRegenerationModel(String(settingsDoc?.REGEN_DEFAULT_MODEL || '').trim());
        setRegenerationModelOptions(extractEnabledModelOptions(settingsDoc || {}));
      } catch (_) {
        if (!isMounted) return;
        setDefaultRegenerationProvider('vertex');
        setDefaultRegenerationModel('');
        setRegenerationModelOptions([]);
      }
    };

    loadRegenerationModelSettings();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    setCurrentImageIndex(0); // Reset index when closing
    setCustomPrompts({}); // Reset custom prompts
    setSelectedModelsBySceneKey({}); // Reset custom model selections
    setRegenerationMetaBySceneKey({}); // Reset regeneration metadata badges
    setExpandedCustomPrompt({}); // Reset expanded states
  };

  const handleDownloadPDF = async () => {
    if (selectedPreview && selectedPreview.generation_id) {
      setIsDownloading(true);
      try {
        // Call the PDF download endpoint
        const response = await fetch(`${SERVER_URL}/previews/download/readablepdf/${selectedPreview.generation_id}`, {
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          // Try to parse the error response body
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }

          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success && result.data && result.data.pdf_readable_url) {
          const pdfUrl = result.data.pdf_readable_url;
          const filename = result.data.filename || `preview-${selectedPreview.generation_id}.pdf`;

          // Check if the URL is from Azure Blob Storage (pre-signed URL)
          const isAzureBlobUrl = pdfUrl.includes('blob.core.windows.net');

          // Create download link immediately without fetching the blob
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = filename;
          link.style.display = 'none';

          // Add target="_blank" for Azure Blob URLs to ensure download behavior
          if (isAzureBlobUrl) {
            link.target = '_blank';
          }

          // Trigger download immediately
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Show success message immediately
          setToastMessage('Download started successfully');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);

        } else {
          // Handle error response from API
          const errorMessage = result.error || result.message || 'Failed to get PDF download link';
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to download PDF file:', error);
        const errorMessage = error.message || 'Failed to download PDF file';
        setToastMessage(`Download Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const handleDownloadPDF38x19 = async () => {
    if (selectedPreview && selectedPreview.generation_id) {
      setIsDownloading38x19(true);
      try {
        const response = await fetch(`${SERVER_URL}/previews/download/readablepdf38x19/${selectedPreview.generation_id}`, {
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success && result.data && result.data.pdf_readable_url) {
          const pdfUrl = result.data.pdf_readable_url;
          const filename = result.data.filename || `preview-${selectedPreview.generation_id}-38x19.pdf`;
          const isAzureBlobUrl = pdfUrl.includes('blob.core.windows.net');

          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = filename;
          link.style.display = 'none';
          if (isAzureBlobUrl) {
            link.target = '_blank';
          }
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setToastMessage('Download (38×19) started successfully');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);
        } else {
          const errorMessage = result.error || result.message || 'Failed to get PDF download link';
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to download PDF (38x19) file:', error);
        const errorMessage = error.message || 'Failed to download PDF file';
        setToastMessage(`Download Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsDownloading38x19(false);
      }
    }
  };

  const handleRegenerateScene = async () => {
    const currentScene = selectedPreview?.generated_scenes?.[currentImageIndex];
    if (!selectedPreview || !currentScene) return;
    if (currentScene?.type !== 'Scene') return;

    const generationId = selectedPreview.generation_id || '';
    const sceneOrder = String(currentScene.sceneOrder ?? '');
    const kid_image = selectedPreview.kid_image || '';
    const story_slug = selectedPreview.story_slug || (selectedPreview.story && selectedPreview.story.slug) || '';

    if (!generationId || !sceneOrder || !kid_image || !story_slug) {
      setToastMessage('Missing data to regenerate scene');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    const sceneKey = `${generationId}::${sceneOrder}`;
    if (activeSceneRegenerationsRef.current.has(sceneKey)) return;
    activeSceneRegenerationsRef.current.add(sceneKey);
    setRegeneratingBySceneOrder(prev => ({ ...prev, [sceneKey]: true }));
    try {
      const customPrompt = customPrompts[sceneKey] || '';
      const customModel = selectedModelsBySceneKey[sceneKey] || '';
      const resp = await fetch(`${SERVER_URL}/regenerate-scene`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ generationId, sceneOrder, kid_image, story_slug, customPrompt, customModel }),
      });
      let data = {};
      try { data = await resp.clone().json(); } catch { }
      if (!resp.ok) {
        const msg = data.error || data.message || `HTTP error! status: ${resp.status}`;
        throw new Error(msg);
      }
      const newUrl = data?.imageUrl || '';
      if (newUrl && sceneOrder) {
        setRegeneratedImageUrlsBySceneOrder((prev) => ({ ...prev, [sceneKey]: newUrl }));
      }
      const providerUsed = String(data?.providerUsed || '').trim();
      const modelSource = String(data?.modelSource || '').trim();
      const resolvedModel = getResolvedModelLabel(data);
      if (providerUsed || modelSource || resolvedModel) {
        setRegenerationMetaBySceneKey((prev) => ({
          ...prev,
          [sceneKey]: {
            providerUsed,
            modelSource,
            resolvedModel,
          },
        }));
      }
      setToastMessage('Regeneration for scene ' + sceneOrder + ' Done');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 2000);
    } catch (err) {
      const msg = err?.message || 'Failed to regenerate scene';
      setToastMessage(`Error: ${msg}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    } finally {
      activeSceneRegenerationsRef.current.delete(sceneKey);
      setRegeneratingBySceneOrder(prev => ({ ...prev, [sceneKey]: false }));
    }
  };

  const showToast = (message, duration = 3000) => {
    setToastMessage(message);
    setToastActive(true);
    setTimeout(() => setToastActive(false), duration);
  };

  const handleEditScene = async () => {
    const currentScene = selectedPreview?.generated_scenes?.[currentImageIndex];
    if (!selectedPreview || !currentScene || currentScene.type !== 'Scene') return;

    const generationId = selectedPreview.generation_id || '';
    const sceneOrder = String(currentScene.sceneOrder ?? '');
    const sceneKey = `${generationId}::${sceneOrder}`;
    const prompt = (editPromptsBySceneKey[sceneKey] || '').trim();
    if (!generationId || !sceneOrder) return;
    if (!prompt) {
      showToast('Enter an edit prompt first');
      return;
    }
    if (activeSceneEditsRef.current.has(sceneKey)) return;
    activeSceneEditsRef.current.add(sceneKey);
    setEditingBySceneKey(prev => ({ ...prev, [sceneKey]: true }));
    try {
      const resp = await fetch(`${SERVER_URL}/edit-scene`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          generationId,
          sceneOrder,
          prompt,
          customModel: editModelsBySceneKey[sceneKey] || '',
        }),
      });
      let data = {};
      try { data = await resp.clone().json(); } catch { }
      if (!resp.ok) {
        throw new Error(data.error || data.message || `HTTP error! status: ${resp.status}`);
      }
      if (data?.imageUrl) {
        setRegeneratedImageUrlsBySceneOrder(prev => ({ ...prev, [sceneKey]: data.imageUrl }));
      }
      setRegenerationMetaBySceneKey(prev => ({
        ...prev,
        [sceneKey]: {
          providerUsed: String(data?.providerUsed || '').trim(),
          modelSource: String(data?.modelSource || '').trim(),
          resolvedModel: getResolvedModelLabel(data),
        },
      }));
      setEditPromptsBySceneKey(prev => ({ ...prev, [sceneKey]: '' }));
      showToast(`Edit for scene ${sceneOrder} done`, 2000);
    } catch (err) {
      showToast(`Error: ${err?.message || 'Failed to edit scene'}`, 4000);
    } finally {
      activeSceneEditsRef.current.delete(sceneKey);
      setEditingBySceneKey(prev => ({ ...prev, [sceneKey]: false }));
    }
  };

  const renderRegenerationMetaBadges = (sceneKey) => {
    const meta = regenerationMetaBySceneKey[sceneKey];
    if (!meta) return null;
    const providerText = providerLabel(meta.providerUsed);
    const modelLabel = meta.resolvedModel || 'provider default';
    const sourceLabel = meta.modelSource ? `Source: ${meta.modelSource}` : '';

    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={badgeStyle}>{`Provider: ${providerText}`}</span>
        <span style={badgeStyle}>{`Model: ${modelLabel}`}</span>
        {sourceLabel ? <span style={badgeStyle}>{sourceLabel}</span> : null}
      </div>
    );
  };

  if (!selectedPreview) return null;

  const storyTitle = String(
    selectedPreview?.story?.title ||
    selectedPreview?.story_title ||
    selectedPreview?.story?.slug ||
    selectedPreview?.story_slug ||
    ''
  ).trim() || 'Unknown story';
  const storyProviderOverride = String(
    selectedPreview?.story?.regeneration_provider_override ||
    selectedPreview?.regeneration_provider_override ||
    ''
  ).trim();
  const storyModelOverride = String(
    selectedPreview?.story?.regeneration_model_override ||
    selectedPreview?.regeneration_model_override ||
    ''
  ).trim();
  const effectiveStoryProvider = normalizeProviderValue(storyProviderOverride) || defaultRegenerationProvider || 'vertex';
  const effectiveStoryModel = storyModelOverride || defaultRegenerationModel || 'provider default';

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span>Generated Scenes Preview</span>
            <span style={badgeStyle}>{`Story: ${storyTitle}`}</span>
            <span style={badgeStyle}>{`Provider: ${providerLabel(effectiveStoryProvider)}`}</span>
            <span style={badgeStyle}>{`Model: ${effectiveStoryModel}`}</span>
          </div>
        </div>
      }
      primaryAction={{
        content: 'Close',
        onAction: handleClose,
      }}
      secondaryActions={[
        {
          content: 'Download PDF',
          icon: <Icon source={FileMinor} />,
          onAction: handleDownloadPDF,
          loading: isDownloading,
        },
        {
          content: 'Download PDF (38×19)',
          icon: <Icon source={FileMinor} />,
          onAction: handleDownloadPDF38x19,
          loading: isDownloading38x19,
        },
      ]}
      footer={
        selectedPreview.generated_scenes && selectedPreview.generated_scenes.length > 1 ? (
          <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            {/* Left: Navigation and info */}
            <div style={{ flex: 1 }}>
              <InlineStack align="center" gap="100">
                <Button
                  icon={ChevronLeftMinor}
                  size="slim"
                  onClick={() => setCurrentImageIndex(prev =>
                    prev === 0 ? selectedPreview.generated_scenes.length - 1 : prev - 1
                  )}
                />
                <Text variant="bodyMd" tone="subdued">
                  {currentImageIndex + 1} of {selectedPreview.generated_scenes.length}
                </Text>
                <Button
                  icon={ChevronRightMinor}
                  size="slim"
                  onClick={() => setCurrentImageIndex(prev =>
                    prev === selectedPreview.generated_scenes.length - 1 ? 0 : prev + 1
                  )}
                />
              </InlineStack>
            </div>
          </div>
        ) : (
          <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div style={{ flex: 1 }}></div>
          </div>
        )
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '16px', alignItems: 'start' }}>
        {/* Left fixed kid image */}
        <div style={{ width: 220 }}>
          <div style={{
            width: '100%',
            height: 400,
            backgroundColor: '#f6f6f7',
            borderRadius: 8,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #ebeef0'
          }}>
            {selectedPreview.kid_image ? (
              <AuthenticatedAssetImage
                src={selectedPreview.kid_image}
                assetSize="thumbnail"
                alt={selectedPreview.kid_name || 'Kid Image'}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <BlockStack gap="100" align="center">
                <Icon source={ImageMajor} color="subdued" />
                <Text variant="bodySm" tone="subdued">No kid image</Text>
              </BlockStack>
            )}
          </div>
        </div>

        {/* Right: scenes carousel/content */}
        <div>
          {selectedPreview.generated_scenes && selectedPreview.generated_scenes.length > 0 ? (
            <BlockStack gap="400">
              {/* Image Display */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f6f6f7',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <AuthenticatedAssetImage
                  src={(function () {
                    const current = selectedPreview.generated_scenes[currentImageIndex];
                    const orderKey = String(current.sceneOrder ?? currentImageIndex);
                    const genId = selectedPreview.generation_id || '';
                    const sceneKey = `${genId}::${orderKey}`;
                    return regeneratedImageUrlsBySceneOrder[sceneKey] || current.processedImageUrl;
                  })()}
                  assetSize="preview"
                  alt={`Scene ${selectedPreview.generated_scenes[currentImageIndex].sceneOrder || currentImageIndex + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>

              {/* Scene Info + per-scene regenerate */}
              <div style={{ textAlign: 'center' }}>
                <Text variant="bodyMd">
                  Scene {selectedPreview.generated_scenes[currentImageIndex].sceneOrder || currentImageIndex + 1}
                </Text>
                <Text variant="bodyMd" tone="subdued">
                  {(() => {
                    const sceneOrder = selectedPreview.generated_scenes[currentImageIndex].sceneOrder;
                    const sceneType = selectedPreview.generated_scenes[currentImageIndex].sceneType;
                    const type = selectedPreview.generated_scenes[currentImageIndex].type;

                    // If scene order is 0, show "Cover"
                    if (sceneOrder === "0" || sceneOrder === 0) {
                      return "Cover";
                    }

                    // If type is "Scene", show "Scene"
                    if (type === "Scene") {
                      return "Scene";
                    }

                    // If sceneType is "text", show "Text"
                    if (sceneType === "text") {
                      return "Text";
                    }

                    // Fallback to sceneType or type if available
                    return sceneType || type || "";
                  })()}
                </Text>
                {(function () {
                  const current = selectedPreview.generated_scenes[currentImageIndex];
                  if (!current || current.type !== 'Scene') return null;
                  const orderKey = String(current.sceneOrder ?? currentImageIndex);
                  const genId = selectedPreview.generation_id || '';
                  const sceneKey = `${genId}::${orderKey}`;
                  const isExpanded = expandedCustomPrompt[sceneKey] || false;
                  return (
                    <BlockStack gap="200" align="center">
                      <div style={{ marginTop: 8 }}>
                        <InlineStack gap="200" align="center">
                          <Button
                            onClick={() => {
                              const willExpand = !isExpanded;
                              setExpandedCustomPrompt(prev => ({ ...prev, [sceneKey]: willExpand }));
                            }}
                            plain
                          >
                            {isExpanded ? 'Hide Custom Face Prompt Option' : 'Edit Face Prompt (Optional)'}
                          </Button>
                          <Button
                            icon={MagicMajor}
                            onClick={handleRegenerateScene}
                            loading={!!regeneratingBySceneOrder[sceneKey]}
                          >
                            Regenerate
                          </Button>
                          <Button
                            onClick={() => setExpandedEditBySceneKey(prev => ({ ...prev, [sceneKey]: !prev[sceneKey] }))}
                          >
                            Edit
                          </Button>
                        </InlineStack>
                      </div>
                      {renderRegenerationMetaBadges(sceneKey)}
                      <Collapsible open={!!expandedEditBySceneKey[sceneKey]} id={`edit-scene-${sceneKey}`}>
                        <div style={{ marginTop: '8px', width: '300px', textAlign: 'left' }}>
                          <BlockStack gap="200">
                            <Select
                              label="Edit Model"
                              options={regenerationModelSelectOptions}
                              value={editModelsBySceneKey[sceneKey] || ''}
                              onChange={(value) => setEditModelsBySceneKey(prev => ({ ...prev, [sceneKey]: value }))}
                            />
                            <TextField
                              label="Edit Prompt"
                              value={editPromptsBySceneKey[sceneKey] || ''}
                              onChange={(val) => setEditPromptsBySceneKey(prev => ({ ...prev, [sceneKey]: val }))}
                              multiline={3}
                              autoComplete="off"
                              helpText="Describe the small change to make. The current generated scene is used as the starting image."
                            />
                            <Button
                              variant="primary"
                              onClick={handleEditScene}
                              loading={!!editingBySceneKey[sceneKey]}
                              disabled={!(editPromptsBySceneKey[sceneKey] || '').trim()}
                            >
                              Apply Edit
                            </Button>
                          </BlockStack>
                        </div>
                      </Collapsible>
                      <Collapsible open={isExpanded} id={`custom-prompt-${sceneKey}`}>
                        <div style={{ marginTop: '8px', width: '300px', textAlign: 'left' }}>
                          <Select
                            label="Regeneration model"
                            options={regenerationModelSelectOptions}
                            value={selectedModelsBySceneKey[sceneKey] || ''}
                            onChange={(value) => setSelectedModelsBySceneKey(prev => ({ ...prev, [sceneKey]: value }))}
                            helpText={defaultRegenerationModel ? `Default: ${defaultRegenerationModel}` : 'Choose a model or keep default.'}
                          />
                        </div>
                        <div style={{ marginTop: '8px', width: '300px', textAlign: 'left' }}>
                          <TextField
                            label="Custom Face Extraction Prompt"
                            value={customPrompts[sceneKey] || ''}
                            onChange={(val) => setCustomPrompts(prev => ({ ...prev, [sceneKey]: val }))}
                            multiline={3}
                            helpText="Leave empty to use the default face prompt or cached face. Modifying this forces a new face generation."
                          />
                        </div>
                      </Collapsible>
                    </BlockStack>
                  );
                })()}
              </div>
            </BlockStack>
          ) : (
            <BlockStack gap="400" align="center">
              <Icon source={ImageMajor} color="subdued" />
              <Text variant="bodyMd" tone="subdued">No generated scenes available</Text>
            </BlockStack>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ViewPreviewModal; 
